import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { USERS } from "../../config/apiConfig";
import DrawerLoading from "../DrawerLoading";
import ForbiddenQueriesPermission from "../common/ForbiddenQueriesPermission";
import { FaSearch, FaSearchDollar, FaTools } from "react-icons/fa";
import { motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import CustomCadastralQuery from "../queries/CustomCadastralQuery";
import { formatToReal } from "../../utils/utils";
import { useApplication } from "../../contexts/ApplicationContext";
import { HiUser, HiOfficeBuilding } from "react-icons/hi";

const truncateText = (value, limit = 80) => {
  if (!value) return "";
  return value.length > limit ? `${value.slice(0, limit - 3)}...` : value;
};

const QueriesCadastralTable = () => {
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isListVisible, setIsListVisible] = useState(true);
  const componentRef = useRef(null);

  const { isLoading, data: userQueryPermissions } = useQuery(
    ["userQueryPermissions", "cadastral"],
    async () => {
      const { data: items } = await axios.get(USERS.GET_USER_QUERY_PERMISSIONS);
      return items.data.filter(
        (query) =>
          query.categoryId === 2 && query.status != -1 && !query.isModular
      );
    },
    {
      refetchOnMount: "always",
      staleTime: 0,
    }
  );

  const filteredQueries = userQueryPermissions?.filter((query) =>
    query?.name?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const handleQueryClick = (query) => {
    setSelectedQuery(query);
    setSelectedPrice(query.price);
    setIsListVisible(false);
    setTimeout(() => {
      componentRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  const handleBackClick = () => {
    setIsListVisible(true);
    setSelectedQuery(null);
  };

  const QueryCard = memo(function QueryCard({ query, index }) {
    const { user } = useApplication();
    const statusActive = query?.status == 1;
    const tags = useMemo(
      () =>
        (query?.tags || [])
          .map((tag) => (typeof tag === "string" ? tag : tag?.name))
          .filter(Boolean)
          .slice(0, 3),
      [query]
    );
    const priceLabel =
      user?.Company?.showPriceQuery && Number.isFinite(Number(query?.price))
        ? `${formatToReal(query.price)}/consulta`
        : "";
    const type = query?.QueryCustomized?.parameterType;
    const TypeIcon = type === "CNPJ" ? HiOfficeBuilding : HiUser;
    const dual = type === "CPF/CNPJ";

    return (
      <motion.div
        key={query.id}
        initial={{ opacity: 0, scale: 0.97, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.04 }}
        onClick={statusActive ? () => handleQueryClick(query) : undefined}
        className={`relative group rounded-3xl border bg-white p-4 shadow-sm transition-all duration-300 ${
          statusActive
            ? "border-slate-200 hover:-translate-y-1 hover:shadow-md cursor-pointer"
            : "border-gray-300 opacity-60 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <span
              className={`rounded-full p-2 ${
                statusActive ? "bg-blue-50" : "bg-gray-100"
              }`}
            >
              {dual ? (
                <div className="flex items-center gap-1 text-base">
                  <HiUser
                    className={`text-base ${
                      statusActive ? "text-blue-700" : "text-gray-500"
                    }`}
                  />
                  <HiOfficeBuilding
                    className={`text-base ${
                      statusActive ? "text-blue-700" : "text-gray-500"
                    }`}
                  />
                </div>
              ) : (
                <TypeIcon
                  className={`text-xl ${
                    statusActive ? "text-blue-700" : "text-gray-500"
                  }`}
                />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <h3
                className="text-[13px] font-semibold text-slate-900 uppercase truncate overflow-hidden max-w-[220px]"
                title={query.name}
              >
                {query.name}
              </h3>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                {type || "CADASTRAL"}
              </p>
            </div>
          </div>
        </div>

        <p className="mt-2 text-[11px] text-slate-500">
          {truncateText(
            query?.description ||
              "Consulta completa com informações oficiais e referências atualizadas.",
            80
          )}
        </p>

        <div className="mt-4 flex items-center justify-between text-[12px] text-slate-500">
          <span className="font-semibold text-slate-900">{priceLabel}</span>
          {statusActive && (
            <span className="text-[11px] font-semibold text-white rounded-full bg-gradient-to-r from-blue-600 to-blue-800 px-3 py-1 shadow-sm">
              CONSULTAR
            </span>
          )}
        </div>
      </motion.div>
    );
  });

  const componentsMap = null;

  if (isLoading) return <DrawerLoading />;
  if (!userQueryPermissions?.length) return <ForbiddenQueriesPermission />;

  return (
    <div className="w-full">
      {isListVisible ? (
        <>
          <div className="mb-3 relative">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar consultas cadastrais..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-600 focus:outline-none text-sm sm:text-base placeholder-gray-400 transition-all"
              />
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-700 pointer-events-none">
                <FaSearch size={18} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 mb-6 bg-gray-50 rounded-md p-4 shadow-sm border border-gray-200">
            {filteredQueries?.map((query, index) => (
              <QueryCard query={query} index={index} key={query.id} />
            ))}
          </div>
        </>
      ) : (
        <div ref={componentRef}>
          <button
            onClick={handleBackClick}
            className="flex items-center text-sm text-green-700 hover:text-green-900 font-medium mb-4 transition-all"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar para a listagem
          </button>

          {selectedQuery?.queryCustomizedId ? (
            <CustomCadastralQuery
              query={selectedQuery}
              selectedPrice={selectedPrice}
            />
          ) : (
            componentsMap[selectedQuery.id] || (
              <div className="text-gray-500 text-center mt-4">
                Componente não encontrado para esta consulta.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default QueriesCadastralTable;
